/**
 * enforceTrailingSlashToUrlOrRoute
 * makes sure the full or partial url or routes
 * have the correct slashes structure
 * It works only for valid urls or routes. It won't fix invalid values
 */

// Adds a leading forward slash to a string, if not present
// this is useful for routes
const getStringWithLeadingSlash = (value: string): string => {
  if (value.slice(0, 1) !== '/') {
    return `/${value}`;
  }
  return value;
};

// Adds a trailing slash to a string, if not present
const getStringWithTrailingSlash = (value: string): string => {
  if (value.slice(-1) !== '/') {
    return `${value}/`;
  }
  return value;
};

export const enforceTrailingSlashToUrlOrRoute = (urlOrRoute: string): string => {
  // We start with the full passed in url or route...
  let currentUrlOrRoute = urlOrRoute;

  // ...and will extract match by match...
  const protocolRegExp = /^(http(s)?:)?\/\//;
  const domainRegExp = /^(((mailto|tel):.*)|((\w+([-_]+)?\.(\w+([-_]+)?(\.)?)+)|localhost)(:[0-9]+)?)/;
  const routeRegExp = /^((\/?(\w+|[^?&=#]+)\/?)+|\/)/;

  // ...assigning each match to its relevant property in the urlData object
  const urlData = {
    protocol: '',
    domain: '',
    route: '',
    extra: '',
  };

  // extracts protocol, if present, and updates currentUrlOrRoute without it
  currentUrlOrRoute = currentUrlOrRoute.replace(protocolRegExp, (strMatch) => {
    urlData.protocol = strMatch;
    return '';
  });

  // extracts domain, if present, and updates currentUrlOrRoute without it
  currentUrlOrRoute = currentUrlOrRoute.replace(domainRegExp, (strMatch) => {
    urlData.domain = strMatch;
    return '';
  });

  // extracts route, if present, and updates currentUrlOrRoute without it
  currentUrlOrRoute = currentUrlOrRoute.replace(routeRegExp, (strMatch) => {
    // replaces any doubled (or more) slashes for a single slash
    const conformedRoute = strMatch.replace(/\/{2,}/g, '/');
    urlData.route = conformedRoute;
    return '';
  });

  // adds in the remaining queries and anchors as a single string
  urlData.extra = currentUrlOrRoute;

  // if telephone or email return the domain property of urlData
  if (urlData.domain.includes('tel:') || urlData.domain.includes('mailto:')) {
    return urlData.domain;
  }

  // if its just extras (usually anchors for scrolling to), return the extra property
  if (urlData.extra && !urlData.route && !urlData.domain) {
    return urlData.extra;
  }

  // if it contains a dot at the end of the route, we do not add a trailing slash
  // assuming it may be a file extension
  const routeParts = urlData.route.split('/');
  if (routeParts[routeParts.length - 1].includes('.')) {
    return `${urlData.protocol}${urlData.domain}${getStringWithLeadingSlash(urlData.route)}${urlData.extra}`;
  }

  // rebuilds string with protocol, domain and route adding forward slashes where necessary
  return `${getStringWithTrailingSlash(
    `${urlData.protocol}${urlData.domain}${getStringWithLeadingSlash(urlData.route)}`
  )}${urlData.extra}`;
};
